import React, { useState } from "react";
import { TableRow, TableCell, Checkbox, IconButton, Chip, Box, Tooltip, Avatar, AvatarGroup, Typography } from "@mui/material";
import { MoreVert as MoreIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const OptionGroupsTableRow = ({
    item,
    index,
    page,
    perPage,
    isSelected,
    onRowCheckboxClick,
    onMenuOpen,
}) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";

    const [tooltipOpen, setTooltipOpen] = useState(false);

    // Display type labels
    const displayTypeLabels = {
        dropdown: { ar: "قائمة منسدلة", en: "Dropdown" },
        radio: { ar: "أزرار", en: "Radio" },
        color: { ar: "ألوان", en: "Color" },
        image: { ar: "صور", en: "Images" },
    };

    // Get display type label
    const getDisplayTypeLabel = (type) => {
        return displayTypeLabels[type]?.[lng] || type;
    };

    // Option values to display
    const optionValues = item.option_values || [];
    const maxVisibleValues = 3;
    const visibleValues = optionValues.slice(0, maxVisibleValues);
    const remainingCount = optionValues.length - maxVisibleValues;

    // Tooltip content for remaining values
    const remainingValuesTooltip = optionValues
        .slice(maxVisibleValues)
        .map((v) => v.name?.[lng] || v.name?.en || "---")
        .join(", ");

    return (
        <TableRow hover selected={isSelected}>
            <TableCell padding="checkbox">
                <Checkbox
                    checked={isSelected}
                    onChange={(event) => onRowCheckboxClick(event, item.id)}
                />
            </TableCell>

            <TableCell>{index + 1 + (page - 1) * perPage}</TableCell>

            {/* Name */}
            <TableCell>
                {item.name?.[lng] || item.name?.["en"] || "---"}
            </TableCell>

            {/* Code */}
            <TableCell>
                <Chip
                    label={item.code || "---"}
                    size="small"
                    variant="outlined"
                    sx={{ fontFamily: 'monospace' }}
                />
            </TableCell>

            {/* Display Type */}
            <TableCell align="center">
                <Chip
                    label={getDisplayTypeLabel(item.display_type)}
                    color="info"
                    size="small"
                    variant="outlined"
                />
            </TableCell>

            {/* Option Values */}
            <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {item.display_type === 'image' ? (
                        <AvatarGroup max={4} size="small" sx={{ '& .MuiAvatar-root': { width: 30, height: 30, fontSize: 12, border: '2px solid #fff' } }}>
                            {optionValues.map((value) => (
                                <Tooltip key={value.id} title={value.name?.[lng] || value.name?.en || "---"} arrow>
                                    <Avatar
                                        src={value.image?.preview_url || value.image?.thumb_url || value.image?.url}
                                        alt={value.name?.[lng]}
                                    />
                                </Tooltip>
                            ))}
                        </AvatarGroup>
                    ) : item.display_type === 'color' ? (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {visibleValues.map((value) => (
                                <Tooltip key={value.id} title={value.name?.[lng] || value.name?.en || value.value} arrow>
                                    <Box
                                        sx={{
                                            width: 22,
                                            height: 22,
                                            borderRadius: '4px',
                                            backgroundColor: value.value,
                                            border: '1px solid rgba(0,0,0,0.1)',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                        }}
                                    />
                                </Tooltip>
                            ))}
                            {remainingCount > 0 && (
                                <Tooltip
                                    title={remainingValuesTooltip}
                                    open={tooltipOpen}
                                    onOpen={() => setTooltipOpen(true)}
                                    onClose={() => setTooltipOpen(false)}
                                    arrow
                                >
                                    <Typography
                                        variant="caption"
                                        onClick={() => setTooltipOpen(!tooltipOpen)}
                                        sx={{
                                            alignSelf: 'center',
                                            ml: 0.5,
                                            fontWeight: 'bold',
                                            color: 'primary.main',
                                            cursor: 'pointer',
                                            '&:hover': { textDecoration: 'underline' }
                                        }}
                                    >
                                        +{remainingCount}
                                    </Typography>
                                </Tooltip>
                            )}
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {visibleValues.map((value) => (
                                <Chip
                                    key={value.id}
                                    label={value.name?.[lng] || value.name?.en || "---"}
                                    size="small"
                                    variant="soft"
                                    sx={{
                                        height: 20,
                                        fontSize: '0.75rem',
                                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'
                                    }}
                                />
                            ))}
                            {remainingCount > 0 && (
                                <Tooltip
                                    title={remainingValuesTooltip}
                                    open={tooltipOpen}
                                    onOpen={() => setTooltipOpen(true)}
                                    onClose={() => setTooltipOpen(false)}
                                    arrow
                                >
                                    <Chip
                                        label={`+${remainingCount}`}
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        onClick={() => setTooltipOpen(!tooltipOpen)}
                                        sx={{ height: 20, fontSize: '0.7rem', cursor: 'pointer', fontWeight: 'bold' }}
                                    />
                                </Tooltip>
                            )}
                        </Box>
                    )}

                    {optionValues.length === 0 && (
                        <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                            {lng === 'ar' ? 'بدون قيم' : 'No values'}
                        </Typography>
                    )}
                </Box>
            </TableCell>

            {/* Status */}
            <TableCell align="center">
                <Chip
                    label={item.is_active ? (lng === 'ar' ? 'نشط' : 'Active') : (lng === 'ar' ? 'غير نشط' : 'Inactive')}
                    color={item.is_active ? "success" : "default"}
                    size="small"
                    variant="outlined"
                />
            </TableCell>

            <TableCell align="center">
                <IconButton onClick={(e) => onMenuOpen(e, item)}>
                    <MoreIcon />
                </IconButton>
            </TableCell>
        </TableRow>
    );
};



export default OptionGroupsTableRow;
